/**
 * Shared Clamp QC measurement rules for hub persist and read paths (API, import tools, inspection rows).
 */

import type { ClampQcHubSettingsPayload } from "../api/clampqc-hub-settings-strict.js";

/** Floor inductance readings to hundredths (1.704 → 1.70) before band comparison. */
export function clampQcInductanceReadingFlooredToHundredths(inductance: number): number {
  return Math.floor(inductance * 100) / 100;
}

/** Manufacturing-Dashboard alignment: `90-003*` large, `90-002*` small. */
export function clampInspectionSizeFromSku(sku: string): "large" | "small" | "unknown" {
  const trimmed = sku.trim();
  if (trimmed.startsWith("90-003")) {
    return "large";
  }
  if (trimmed.startsWith("90-002")) {
    return "small";
  }
  return "unknown";
}

export function clampQcInductanceAcceptanceRangeForSku(
  sku: string,
  settings: ClampQcHubSettingsPayload,
): { min: number; max: number } | null {
  const line = clampInspectionSizeFromSku(sku);
  if (line === "large") {
    return { min: settings.inductanceMinLarge, max: settings.inductanceMaxLarge };
  }
  if (line === "small") {
    return { min: settings.inductanceMinSmall, max: settings.inductanceMaxSmall };
  }
  return null;
}

/** True when a finite reading is outside the configured band for this SKU line. */
export function clampQcInductanceValueFailsAcceptance(
  inductance: number,
  sku: string,
  settings: ClampQcHubSettingsPayload,
): boolean {
  const range = clampQcInductanceAcceptanceRangeForSku(sku, settings);
  if (range === null) {
    return false;
  }
  const floored = clampQcInductanceReadingFlooredToHundredths(inductance);
  return floored < range.min || floored > range.max;
}

/**
 * `sgs_inductance_pass` for hub persist: null when this unit was not in the stratified sample;
 * false when sampled but missing or out of band; true when sampled and in band.
 */
export function deriveSgsInductancePassForPersist(input: {
  inductanceMeasurementRequired: boolean;
  sgsInductance: number | null;
  sku: string;
  settings: ClampQcHubSettingsPayload;
}): boolean | null {
  const { inductanceMeasurementRequired, sgsInductance, sku, settings } = input;
  if (!inductanceMeasurementRequired) {
    return null;
  }
  if (sgsInductance === null) {
    return false;
  }
  return !clampQcInductanceValueFailsAcceptance(sgsInductance, sku, settings);
}

/** Drag = unloaded line minus loaded-before-tester (floor worksheet rule). */
export function clampQcDragFromLineReadings(unloaded: number, loadedBeforeTester: number): number {
  return unloaded - loadedBeforeTester;
}

export function clampQcUnloadedLineAcceptanceRangeForSku(
  sku: string,
  settings: ClampQcHubSettingsPayload,
): { min: number; max: number } | null {
  const line = clampInspectionSizeFromSku(sku);
  if (line === "large") {
    return { min: settings.unloadedMinLarge, max: settings.unloadedMaxLarge };
  }
  if (line === "small") {
    return { min: settings.unloadedMinSmall, max: settings.unloadedMaxSmall };
  }
  return null;
}

/** True when a finite unloaded reading is outside the inclusive accepted band for this SKU line. */
export function clampQcUnloadedLineFailsAcceptance(
  unloaded: number,
  sku: string,
  settings: ClampQcHubSettingsPayload,
): boolean {
  const range = clampQcUnloadedLineAcceptanceRangeForSku(sku, settings);
  if (range === null) {
    return false;
  }
  return unloaded < range.min || unloaded > range.max;
}

export type ClampQcDragBand = "fail" | "gray" | "pass" | "unknown";

export type ClampQcDragLadder = {
  grayMin: number;
  passMin: number;
  passMax: number;
  grayMax: number;
};

export function clampQcDragLadderFromSettings(
  sku: string,
  settings: ClampQcHubSettingsPayload,
): ClampQcDragLadder | null {
  const line = clampInspectionSizeFromSku(sku);
  if (line === "large") {
    return {
      grayMin: settings.dragGrayMinLarge,
      passMin: settings.dragPassMinLarge,
      passMax: settings.dragPassMaxLarge,
      grayMax: settings.dragGrayMaxLarge,
    };
  }
  if (line === "small") {
    return {
      grayMin: settings.dragGrayMinSmall,
      passMin: settings.dragPassMinSmall,
      passMax: settings.dragPassMaxSmall,
      grayMax: settings.dragGrayMaxSmall,
    };
  }
  return null;
}

export function clampQcDragBandForDrag(drag: number | null, ladder: ClampQcDragLadder | null): ClampQcDragBand {
  if (ladder == null) {
    return "unknown";
  }
  if (drag == null || !Number.isFinite(drag)) {
    return "unknown";
  }
  if (drag < ladder.grayMin || drag > ladder.grayMax) {
    return "fail";
  }
  if (drag >= ladder.passMin && drag <= ladder.passMax) {
    return "pass";
  }
  return "gray";
}

/**
 * `sgs_line_harvest_pass` for hub persist: true/false when drag rules or the operator decide;
 * null when the gray-band Good/Failed choice was not made (for example mechanical-only fail).
 */
export function deriveSgsLineHarvestPassForPersist(input: {
  lineHarvestFailManual: boolean | null;
  drag: number | null;
  sku: string;
  settings: ClampQcHubSettingsPayload;
}): boolean | null {
  const ladder = clampQcDragLadderFromSettings(input.sku, input.settings);
  if (ladder == null) {
    return input.lineHarvestFailManual === null ? null : !input.lineHarvestFailManual;
  }
  const band = clampQcDragBandForDrag(input.drag, ladder);
  if (band === "fail") {
    return false;
  }
  if (band === "pass") {
    return true;
  }
  if (band === "gray") {
    return input.lineHarvestFailManual === null ? null : !input.lineHarvestFailManual;
  }
  return input.lineHarvestFailManual === null ? null : !input.lineHarvestFailManual;
}

/** True only when line harvest is a definite fail (null = not judged, not a fail). */
export function clampQcLineHarvestFailsFromDragDraft(
  lineHarvestFailManual: boolean | null,
  drag: number | null,
  sku: string,
  settings: ClampQcHubSettingsPayload | null,
): boolean {
  if (settings == null) {
    return lineHarvestFailManual === null ? false : lineHarvestFailManual;
  }
  return (
    deriveSgsLineHarvestPassForPersist({
      lineHarvestFailManual,
      drag,
      sku,
      settings,
    }) === false
  );
}
